/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import TurnstileWidget from "@/components/TurnstileWidget";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsRef = useRef(null);
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (turnstileEnabled && !tsToken) {
      toast.error(
        "Verification not ready",
        {
          description: "Please tick the 'Verify you are human' box before sending. If you can't see it, refresh the page or check that your network isn't blocking challenges.cloudflare.com.",
        },
      );
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, turnstile_token: tsToken });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" ? detail : "Failed");
      toast.error(msg);
      try { tsRef.current?.reset(); setTsToken(""); } catch { /* ignore */ }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
      <header className="border-b border-[#D9E1E3] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="forgot-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-[#D9E1E3] bg-white rounded-md p-6 md:p-8" data-testid="forgot-card">
          <h1 className="font-heading text-2xl font-semibold">Reset your password</h1>
          <p className="text-sm text-[#6F828A] mt-1">Enter your email and we'll send you a secure reset link. The link expires in 30 minutes.</p>

          {sent ? (
            <div className="mt-6 border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm p-4 text-sm text-[#2E3A3F]" data-testid="forgot-sent-message">
              If an account exists for <span className="font-mono-data">{email}</span>, a reset link has been sent. Please check your inbox and spam folder.
              <div className="mt-4">
                <Button variant="outline" onClick={() => navigate("/auth")} className="border-[#D9E1E3] rounded-sm" data-testid="forgot-back-signin">Back to sign in</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <TurnstileWidget
                ref={tsRef}
                action="forgot-password"
                testId="forgot-turnstile"
                onToken={setTsToken}
              />
              {turnstileEnabled && !tsToken && (
                <p className="text-[11px] text-[#6F828A]" data-testid="forgot-turnstile-hint">
                  Tick the verification box above to enable sending.
                </p>
              )}
              <Button
                type="submit"
                disabled={busy || (turnstileEnabled && !tsToken)}
                className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
                data-testid="forgot-submit-button"
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-center text-sm">
                <Link to="/auth" className="text-[#6F828A] hover:text-[#2E3A3F]" data-testid="forgot-back-link">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
